import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { IdH4 } from '../IdHead';
import { ACTIVE, EXPIRED } from '../../constants';
import { MMDDYYYY } from '../../utils/time';
import IdProps from '../../proptypes/id';
import style from '../../style/components/overview/Ids.module.scss';

const IdList = ({ active, expired, children }) => {
  const { t } = useTranslation();
  const [selected, setSelected] = useState({ id: -1 });
  const [type, setType] = useState(ACTIVE);

  // filter out expired IDs for groups that have an active ID
  const activeGroups = active.map((d) => d.type);
  const showExpired = expired.filter((id) => {
    return !activeGroups.includes(id.type);
  });

  const noActive = active.length === 0;
  const noExpired = showExpired.length === 0;

  if (!children && noActive && noExpired) {
    return (
      <>
        <div className={style.no}>
          <h4>No linked IDs yet...</h4>
          <p>{t('overview.ids.description_empty')}</p>
        </div>
        {children}
        <div className={style.manage}>
          <Link to="/wallet/ids">Manage IDs</Link>
        </div>
      </>
    );
  }

  return (
    <div className={style.list}>
      <ul>
        {active
          .filter((credential) => credential.id !== selected.id)
          .map((credential, index) => (
            <li key={credential.type} className={style.idCard}>
              <button
                type="button"
                onClick={() => {
                  setSelected(credential);
                  setType(ACTIVE);
                }}
              >
                <IdH4
                  {...credential}
                  iconUrl={credential.credential_icon_url}
                />
              </button>
              {/* only render the body if it is the last one and there are no expired ones and none are selected */}
              {noExpired &&
                index === active.length - 1 &&
                selected.id === -1 && (
                  <div className={style.cardBottom}>
                    <div className={style.cardDescription}>
                      {credential.title}
                    </div>
                    <div className={style.cardDate}>
                      <div>VERIFIED</div>
                      <div>{MMDDYYYY(credential.verified)}</div>
                    </div>
                  </div>
                )}
            </li>
          ))}
        {showExpired
          .filter((credential) => credential.id !== selected.id)
          .map((credential, index) => (
            <li key={credential.type} className={style.idCard}>
              <button
                type="button"
                onClick={() => {
                  setSelected(credential);
                  setType(EXPIRED);
                }}
              >
                <IdH4
                  {...credential}
                  iconUrl={credential.credential_icon_url}
                />
              </button>
              {/* only render the body if it is the last one and none are selected */}
              {index === showExpired.length - 1 && selected.id === -1 && (
                <div className={style.cardBottom}>
                  <div className={style.cardDescription}>
                    {credential.title}
                  </div>
                  <div className={style.cardDate}>
                    <div className={style.expired}>EXPIRED</div>
                    <div>{MMDDYYYY(credential.expires)}</div>
                  </div>
                </div>
              )}
            </li>
          ))}
        {selected.id !== -1 && (
          <li key={selected.type} className={style.idCard}>
            <button>
              <IdH4 {...selected} iconUrl={selected.credential_icon_url} />
            </button>
            {type === ACTIVE ? (
              <div className={style.cardBottom}>
                <div className={style.cardDescription}>{selected.title}</div>
                <div className={style.cardDate}>
                  <div>VERIFIED</div>
                  <div>{MMDDYYYY(selected.verified)}</div>
                </div>
              </div>
            ) : (
              <div className={style.cardBottom}>
                <div className={style.cardDescription}>{selected.title}</div>
                <div className={style.cardDate}>
                  <div className={style.expired}>EXPIRED</div>
                  <div>{MMDDYYYY(selected.expires)}</div>
                </div>
              </div>
            )}
          </li>
        )}
        {/* Only continue the fanned-deck overlap when there's an actual
        preceding card to fan under -- otherwise (0 active/expired) this
        card is the first thing in the list and the same negative margin
        just pulls it up into the title above, by an amount that happens
        to depend on unrelated page layout (e.g. how tall the profile-top
        block next to it renders), not on this section's own content. */}
        {children && (
          <li
            className={
              noActive && noExpired
                ? style.idCard
                : `${style.idCard} ${style.idCardFanned}`
            }
          >
            {children}
          </li>
        )}
      </ul>
      <div className={style.manage}>
        <Link to="/wallet/ids">Manage IDs</Link>
      </div>
    </div>
  );
};

IdList.propTypes = {
  active: PropTypes.arrayOf(PropTypes.shape(IdProps)),
  expired: PropTypes.arrayOf(PropTypes.shape(IdProps)),
  children: PropTypes.node,
};

IdList.defaultProps = {
  active: [],
  expired: [],
  children: null,
};

export default IdList;
